import React, { useState } from "react";
import { Card } from "reactstrap";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, ReferenceLine } from "recharts";
import { DatePicker, Spin } from "antd";
import { LineChartOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useQuery } from "react-query";
import { getTurnOverByBusinessId } from "../../developer/services/developerApi";
import "../utils/style.css";

const TurnOver = ({ title = "", setAmountForHome }) => {
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  const currentUser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchandId = currentUser?.user?.id || currentUser?.user?.merchantId;

  const { data: turnoverData = [], isLoading } = useQuery(
    ["turnover", merchandId, selectedYear],
    () => getTurnOverByBusinessId(merchandId, selectedYear),
    { enabled: !!merchandId }
  );

  const amount = turnoverData.reduce((sum, item) => sum + item.value, 0);

  React.useEffect(() => {
    if (typeof setAmountForHome === "function") {
      setAmountForHome(amount);
    }
  }, [amount, setAmountForHome]);

  const bestMonth = turnoverData.length > 0 ? turnoverData.reduce((prev, current) => (prev.value > current.value ? prev : current)).month : null;

  const isAllZero = turnoverData.length > 0 && turnoverData.every((d) => d.value === 0);

  const handleChangeYear = (date) => {
    if (date) {
      const year = dayjs(date).year();
      setSelectedYear(year);
    }
  };

  return (
    <Card className="p-4 transfert-card">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold">{title}</h5>
        <DatePicker
          onChange={handleChangeYear}
          picker="year"
          value={dayjs(`${selectedYear}`)}
          allowClear={false}
          className="transfert-datepicker"
          disabledDate={(current) => current && current.year() > dayjs().year()}
        />
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={turnoverData} margin={{ left: 20, right: 20 }}>
            <defs>
              <linearGradient id="colorBlue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3761FF" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#E2E8FF" stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} strokeDasharray="3 3" horizontal={!isAllZero} />
            <XAxis dataKey="month" interval={0} padding={{ left: 70, right: 10 }} />
            <YAxis />

            {bestMonth && !isAllZero && (
              <ReferenceLine
                x={bestMonth}
                stroke="#3761FF"
                strokeOpacity={0.15}
                strokeWidth={30}
              />
            )}

            <Tooltip
              contentStyle={{ backgroundColor: "#000", color: "#fff", borderRadius: "5px" }}
              labelFormatter={(label) => `Mois : ${label}`}
              formatter={(val) => [`${val.toLocaleString()} XOF`, { title }]}
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke="#3761FF"
              fill="url(#colorBlue)"
              fillOpacity={1}
            />

            <Line
              type="monotone"
              dataKey="value"
              stroke="#3761FF"
              strokeWidth={3}
              dot={{ r: 5, fill: "#fff", stroke: "#3761FF", strokeWidth: 2 }}
              activeDot={{ r: 8, fill: "#3761FF", stroke: "#fff", strokeWidth: 3 }}
            />

            {isAllZero && (
              <foreignObject x="0" y="50" width="100%" height="200">
                <div className="transfert-empty-message">
                  <LineChartOutlined className="transfert-empty-icon animate-pulse" />
                  <p className="transfert-empty-title">Aucune donnée disponible</p>
                  <small className="transfert-empty-subtext">
                    Aucun {title} pour ce business en <strong>{selectedYear}</strong>
                  </small>
                </div>
              </foreignObject>
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default TurnOver;
